import { useUploadDocument } from "hooks/useUploadDocument";
import { Mic, Sparkles, Square, X } from "lucide-react";
import { useRef, useState } from "react";
import { useAudioRecorder } from "@compass/shared/hooks";
import { ClientDocument } from "@compass/shared/types";
import { Button } from "./Button";
import { Spinner } from "./Spinner";

interface AudioRecorderProps {
    onRecordingComplete: (file: ClientDocument) => void;
    // When true, the recording is uploaded as PUBLIC_ATTACHMENT so other
    // users on a public thread (feed comments, vendor comments, etc.) can
    // play it back. Private discussion attachments default to private.
    isPublic?: boolean;
    /** Where this upload happens, for the admin upload notification (PRTL-1322). */
    notifyContext?: "discussion" | "conversation" | "social" | "documents";
}

const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

export const AudioRecorder = ({
    onRecordingComplete,
    isPublic = false,
    notifyContext,
}: AudioRecorderProps) => {
    const { isRecording, duration, error, startRecording, stopRecording, cancelRecording } =
        useAudioRecorder();
    const uploadDocument = useUploadDocument();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [enableAI, setEnableAI] = useState(false);
    // The toggle stays live while recording, and the upload happens only after
    // Stop, so the upload reads the flag at that moment rather than the value
    // captured when Record was pressed (PRTL-993).
    const enableAIRef = useRef(enableAI);
    enableAIRef.current = enableAI;

    const handleStart = async () => {
        setUploadError(null);
        let file: File;
        try {
            file = await startRecording();
        } catch {
            // Microphone errors are in the hook's `error`; a cancel is not an error.
            return;
        }
        setIsUploading(true);
        try {
            const doc = await uploadDocument({
                filename: file.name,
                file,
                aiOptOut: !enableAIRef.current,
                context: isPublic ? "PUBLIC_ATTACHMENT" : "PRIVATE_ATTACHMENT",
                notifyContext,
            });
            onRecordingComplete(doc);
        } catch {
            // Used to be swallowed, so a failed upload looked like nothing happened.
            setUploadError("The recording could not be uploaded. Please try again.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleStop = () => {
        stopRecording();
    };

    const handleCancel = () => {
        cancelRecording();
    };

    if (isUploading) {
        return (
            <div className="flex items-center gap-2 text-sm text-gray-500">
                <Spinner className="h-4 w-4" />
                <span>Uploading recording...</span>
            </div>
        );
    }

    const aiToggle = (
        <Button
            type="button"
            variant="secondary"
            size="smallest"
            onClick={() => setEnableAI(!enableAI)}
            title={
                enableAI
                    ? "AI transcript on: this recording will be transcribed and summarised"
                    : "AI transcript off: click to have this recording transcribed and summarised"
            }
            aria-pressed={enableAI}
            className={enableAI ? "text-blue-600" : "text-gray-400"}
        >
            <Sparkles className="h-3.5 w-3.5" />
        </Button>
    );

    if (isRecording) {
        return (
            <div className="flex items-center gap-2">
                <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                <span className="text-sm font-medium text-red-600">{formatDuration(duration)}</span>
                <Button
                    type="button"
                    variant="secondary"
                    size="smallest"
                    onClick={handleStop}
                    title="Stop recording"
                    aria-label="Stop recording"
                >
                    <Square className="h-3.5 w-3.5" />
                </Button>
                <Button
                    type="button"
                    variant="secondary"
                    size="smallest"
                    onClick={handleCancel}
                    title="Cancel recording"
                    aria-label="Cancel recording"
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
                {aiToggle}
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div className="flex items-center gap-1">
                <Button
                    type="button"
                    variant="secondary"
                    size="smallest"
                    onClick={handleStart}
                    title="Record audio"
                    aria-label="Record audio"
                >
                    <Mic className="h-4 w-4" />
                </Button>
                {aiToggle}
            </div>
            {(error || uploadError) && (
                <span role="alert" className="mt-1 text-xs text-red-500">
                    {uploadError ?? error}
                </span>
            )}
        </div>
    );
};
